import { MerchantValidationError } from "../error";
import * as Fields from "../fields";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";
import { BusinessType, CountryList } from "../types";

export type CompanyInformationFieldSetValueMap = {
  abn: string;
  businessCity: string;
  businessPostalCode: string;
  businessState: string;
  businessStreet: string;
  businessStreet2: string;
  businessType: BusinessType;
  companyNumber: string;
  craBusinessNumber: string;
  craRegistryId: string;
  ein: string;
  hasConstitution: string;
  hasShareholderAgreement: string;
  legalBusinessName: string;
  partnershipAgreement: string;
  proofOfInsurance: string;
  registeredCountry: CountryList;
  registrationNumber: string;
  stockMarketLister: string;
  trustDeed: string;
};

export type CompanyInformationFieldSetValueMapper<T> = {
  [K in keyof CompanyInformationFieldSetValueMap]: T;
};

export class CompanyInformationFieldSet extends FieldSet<CompanyInformationFieldSetValueMap> {
  static fromJson(
    CompanyInformation: CompanyInformationFieldSet,
    {
      abn,
      businessCity,
      businessPostalCode,
      businessState,
      businessStreet,
      businessStreet2,
      businessType,
      companyNumber,
      craBusinessNumber,
      craRegistryId,
      ein,
      hasConstitution,
      hasShareholderAgreement,
      legalBusinessName,
      partnershipAgreement,
      proofOfInsurance,
      registeredCountry,
      registrationNumber,
      stockMarketLister,
      trustDeed,
    }: Partial<CompanyInformationFieldSetValueMap>
  ): CompanyInformationFieldSet {
    CompanyInformation.abn.value = abn;
    CompanyInformation.businessCity.value = businessCity;
    CompanyInformation.businessPostalCode.value = businessPostalCode;
    CompanyInformation.businessState.value = businessState;
    CompanyInformation.businessStreet.value = businessStreet;
    CompanyInformation.businessStreet2.value = businessStreet2;
    CompanyInformation.businessType.value = businessType;
    CompanyInformation.companyNumber.value = companyNumber;
    CompanyInformation.craBusinessNumber.value = craBusinessNumber;
    CompanyInformation.craRegistryId.value = craRegistryId;
    CompanyInformation.ein.value = ein;
    CompanyInformation.hasConstitution.value = hasConstitution;
    CompanyInformation.hasShareholderAgreement.value = hasShareholderAgreement;
    CompanyInformation.legalBusinessName.value = legalBusinessName;
    CompanyInformation.partnershipAgreement.value = partnershipAgreement;
    CompanyInformation.proofOfInsurance.value = proofOfInsurance;
    CompanyInformation.registeredCountry.value = registeredCountry;
    CompanyInformation.registrationNumber.value = registrationNumber;
    CompanyInformation.stockMarketLister.value = stockMarketLister;
    CompanyInformation.trustDeed.value = trustDeed;
    return CompanyInformation;
  }

  public abn: Fields.AbnField;
  public businessCity: Fields.CityField;
  public businessPostalCode: Fields.PostalCodeField;
  public businessState: Fields.StateField;
  public businessStreet: Fields.StreetField;
  public businessStreet2: Fields.Street2Field;
  public businessType: Fields.BusinessTypeField;
  public companyNumber: Fields.CompanyNumberField;
  public craBusinessNumber: Fields.CraBusinessNumberField;
  public craRegistryId: Fields.CraRegistryIdField;
  public ein: Fields.EinField;
  public hasConstitution: Fields.HasConstitutionField;
  public hasShareholderAgreement: Fields.HasShareholderAgreementField;
  public legalBusinessName: Fields.LegalBusinessNameField;
  public partnershipAgreement: Fields.PartnershipAgreementField;
  public proofOfInsurance: Fields.ProofOfInsuranceField;
  public registeredCountry: Fields.RegisteredCountryField;
  public registrationNumber: Fields.RegistrationNumberField;
  public stockMarketLister: Fields.StockMarketListerField;
  public trustDeed: Fields.TrustDeedField;

  constructor(model: Merchant) {
    super();

    this.fieldSetName = "CompanyInformationFieldSet";

    this.abn = new Fields.AbnField(model);
    this.businessCity = new Fields.CityField(model, null, {
      propertyName: "businessCity",
    });
    this.businessPostalCode = new Fields.PostalCodeField(model, null, {
      propertyName: "businessPostalCode",
    });
    this.businessState = new Fields.StateField(model, null, {
      propertyName: "businessState",
    });
    this.businessStreet = new Fields.StreetField(model, null, {
      propertyName: "businessStreet",
    });
    this.businessStreet2 = new Fields.Street2Field(model, null, {
      propertyName: "businessStreet2",
    });
    this.businessType = new Fields.BusinessTypeField(model);
    this.companyNumber = new Fields.CompanyNumberField(model);
    this.craBusinessNumber = new Fields.CraBusinessNumberField(model);
    this.craRegistryId = new Fields.CraRegistryIdField(model);
    this.ein = new Fields.EinField(model);
    this.hasConstitution = new Fields.HasConstitutionField(model);
    this.hasShareholderAgreement = new Fields.HasShareholderAgreementField(
      model
    );
    this.legalBusinessName = new Fields.LegalBusinessNameField(model);
    this.partnershipAgreement = new Fields.PartnershipAgreementField(model);
    this.proofOfInsurance = new Fields.ProofOfInsuranceField(model);
    this.registeredCountry = new Fields.RegisteredCountryField(model);
    this.registrationNumber = new Fields.RegistrationNumberField(model);
    this.stockMarketLister = new Fields.StockMarketListerField(model);
    this.trustDeed = new Fields.TrustDeedField(model);
    this.fields = [
      this.abn,
      this.businessCity,
      this.businessPostalCode,
      this.businessState,
      this.businessStreet,
      this.businessStreet2,
      this.businessType,
      this.companyNumber,
      this.craBusinessNumber,
      this.craRegistryId,
      this.ein,
      this.hasConstitution,
      this.hasShareholderAgreement,
      this.legalBusinessName,
      this.partnershipAgreement,
      this.proofOfInsurance,
      this.registeredCountry,
      this.registrationNumber,
      this.stockMarketLister,
      this.trustDeed,
    ];
  }

  validate({ stringify = true }: { stringify: boolean }): void {
    const Errors = new MerchantValidationError();

    let errorCount = 0;
    for (const field of this.fields) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;
        if (Errors.fieldErrorMap.companyInformation) {
          Errors.fieldErrorMap.companyInformation[
            field.propertyName as keyof CompanyInformationFieldSetValueMap
          ] = { error: stringify ? ex.message : ex };
        }
      }
    }

    if (errorCount) {
      throw Errors.fieldErrorMap;
    }
  }
}
